import { combineReducers, createAppCacheReducer } from '@fullstory/redux-util/utils';
import { HeatmapRecord } from '@fullstory/proto/fs/services/search-settings';
import { eventTypeToEventState, SearchGroup } from '@fullstory/search-model';
import { HeatmapsById, HeatmapsRequestTypes, HeatmapsState } from './types';
import {
  HeatmapsActions,
  HeatmapsDataType,
  HeatmapsClickAreaDataType,
  HeatmapsClickAreaActions,
} from './actions';
import { createAccessReducer } from '../../utils/accessControl';
import { SearchActions } from '../search';
import { SearchActionTypes } from '../search/types';
import { createRequestReducer, DispatchedAction, RequestAction } from '@fullstory/redux-util';
import {
  CreateUnnamedHeatmapRequest,
  GetAllHeatmapsResponse,
  GetUnnamedHeatmapResponse,
  HeatmapDetail,
  UnnamedHeatmap,
} from '@fullstory/proto/fs/services/dx-objects';
import { Action } from 'redux';
import { Event } from '@fullstory/proto/fs/api/segment';
import { EventType } from '@fullstory/search-types';
import { AppBackendActions, AppBackendRequestTypes } from '../appbackend';
import { SpaceActions, SpaceRequestTypes } from '../spaces';
import { mapKeys } from 'lodash';
import { getObjectWithOverlayStorageId } from '../../utils';
import { HomeRequestActions, HomeRequestTypes } from '../home';
import { BulkActionsRequestTypes } from '../bulkActions/types';
import { BulkRequestActions } from '../bulkActions/actions';

const getHeatmapId = (Heatmap: HeatmapRecord | null): string | undefined => Heatmap?.heatmap?.id;

const initHeatmap = (heatmapDetail: HeatmapDetail): HeatmapDetail => {
  const hm = heatmapDetail.heatmap?.heatmap?.heatmapEvent;
  if (hm) {
    heatmapDetail.heatmap!.heatmap!.heatmapEvent = {
      primary: hm.primary ?? null,
      deps: hm.deps ?? null,
    };
  }
  return heatmapDetail;
};

export const heatmaps = (
  state: HeatmapsState = {},
  action:
    | AppBackendActions
    | SpaceActions
    | HeatmapsActions
    | HeatmapsClickAreaActions
    | HomeRequestActions
    | BulkRequestActions,
): HeatmapsState => {
  switch (action.type) {
    case HeatmapsRequestTypes.ArchiveHeatmapSuccess: {
      const { body } = action.request;

      if (body == null) throw new Error('Invariant violation: Request body must exist.');
      const { heatmapId, archived } = body;
      const heatmap = state?.[heatmapId];
      const heatmapRecord = heatmap?.heatmap;
      const metadata = heatmapRecord?.metadata;

      return heatmap == null || heatmapRecord == null || metadata == null
        ? state
        : {
            ...state,
            [heatmapId]: {
              ...heatmap,
              heatmap: {
                ...heatmapRecord,
                metadata: { ...metadata, archived },
              },
            },
          };
    }
    case BulkActionsRequestTypes.BulkArchiveObjectsSuccess: {
      const { body } = action.request;

      if (!body) return state;

      let newState = { ...state };
      const heatmapsReq = body.heatmaps;

      if (heatmapsReq != null) {
        heatmapsReq.heatmaps.forEach(m => {
          const { heatmapId, archived } = m;
          const heatmap = newState?.[heatmapId];
          const heatmapRecord = heatmap?.heatmap;
          const metadata = heatmapRecord?.metadata;

          if (heatmap && heatmapRecord && metadata) {
            newState = {
              ...newState,
              [heatmapId]: {
                ...heatmap,
                heatmap: {
                  ...heatmapRecord,
                  metadata: { ...metadata, archived },
                },
              },
            };
          }
        });
      }

      return newState;
    }
    case HeatmapsRequestTypes.GetAllHeatmapsSuccess: {
      return action.data.heatmaps.reduce<HeatmapsById>(
        (acc, val) => {
          const id = getHeatmapId(val.heatmap);

          if (id != null) acc[id] = initHeatmap(val);
          return acc;
        },
        { ...state },
      );
    }
    case AppBackendRequestTypes.GetHomeViewSuccess: // fallthrough
    case AppBackendRequestTypes.GetHeatmapDetailViewSuccess: // fallthrough
    case AppBackendRequestTypes.GetRetentionMetricDetailViewSuccess: // fallthrough
    case AppBackendRequestTypes.GetSidebarSuccess: {
      if (!action.data?.deps?.heatmaps) return state;
      return {
        ...state,
        ...action.data?.deps?.heatmaps,
      };
    }
    case HomeRequestTypes.DuplicateHomeCardSuccess: {
      if (action.data.heatmap?.heatmap?.heatmap) {
        return { ...state, [action.data.heatmap.heatmap.heatmap.id]: { ...action.data.heatmap } };
      }
      return state;
    }
    case HeatmapsRequestTypes.MarkViewedSuccess: {
      const { body } = action.request;
      if (body == null) throw new Error('Invariant violation: Request body must exist.');

      const { lastViewed } = action.data;
      const { heatmapId } = body;
      const heatmap = state?.[heatmapId];

      return heatmap == null ? state : { ...state, [heatmapId]: { ...heatmap, lastViewed } };
    }
    case HeatmapsRequestTypes.SetHeatmapsSuccess: {
      const { heatmap: HeatmapRecord } = action.data;
      const id = getHeatmapId(HeatmapRecord);
      if (HeatmapRecord == null || id == null) return state;

      const newState = { ...state };
      const heatmapDetail = initHeatmap(action.data);

      // Update base state
      newState[id] = heatmapDetail;

      // Update any overlay states that exist for this heatmap
      Object.keys(state).forEach(key => {
        if (key.startsWith(`${id}-`)) {
          // This is an overlay state for our heatmap
          const existingOverlay = state[key]!;
          const overlayHeatmap: HeatmapDetail = {
            ...existingOverlay,
            heatmap:
              existingOverlay.heatmap?.heatmap && heatmapDetail.heatmap?.heatmap
                ? {
                    ...heatmapDetail.heatmap,
                    heatmap: {
                      ...heatmapDetail.heatmap.heatmap,
                      heatmapSettings: heatmapDetail.heatmap?.heatmap?.heatmapSettings ?? null,
                      heatmapEvent: heatmapDetail.heatmap?.heatmap?.heatmapEvent ?? null,
                    },
                  }
                : null,
          };
          newState[key] = overlayHeatmap;
        }
      });

      return newState;
    }
    case HeatmapsRequestTypes.StarHeatmapSuccess: {
      const { body } = action.request;

      if (body == null) throw new Error('Invariant violation: Request body must exist.');

      const { heatmapId, starred } = body;
      const heatmap = state?.[heatmapId];

      return heatmap == null ? state : { ...state, [heatmapId]: { ...heatmap, starred } };
    }
    case SpaceRequestTypes.GetSpaceCardViewSuccess: {
      const updated = { ...state };
      const allObjects = action.data.sections.flatMap(s => s.cards.flatMap(c => c.object));

      for (const object of allObjects) {
        if (object?.heatmap && object.heatmap.heatmap?.heatmap?.id) {
          updated[object.heatmap.heatmap.heatmap.id] = object.heatmap;
        }
      }

      return updated;
    }
    case SpaceRequestTypes.GetLibraryViewSuccess: {
      const heatmaps = action.data.objects.reduce<HeatmapsState>((acc, o) => {
        if (o.heatmap?.heatmap?.heatmap?.id) acc[o.heatmap.heatmap?.heatmap?.id] = o.heatmap;
        return acc;
      }, {});

      return Object.keys(heatmaps).length
        ? {
            ...state,
            ...heatmaps,
          }
        : state;
    }
    case AppBackendRequestTypes.GetDashboardDetailViewSuccess:
      if (!action.data.deps?.heatmaps) return state;
      if (action.data.overlay && action.data.deps.heatmaps) {
        const heatmaps = action.data.deps.heatmaps;
        const overlayId = action.data.overlay.id;
        const updatedHeatmaps = mapKeys(heatmaps, (_, key) =>
          getObjectWithOverlayStorageId(key, overlayId),
        );
        return { ...state, ...updatedHeatmaps };
      }
      return { ...state, ...action.data.deps.heatmaps };
  }
  return state;
};

type UnnamedHeatmapsState = { [hash: string]: UnnamedHeatmap };
const unnamedHeatmapsRequests = createRequestReducer<
  UnnamedHeatmapsState,
  GetUnnamedHeatmapResponse
>({
  mergeData: (state, update) => {
    const { hash, heatmap } = update;
    if (!heatmap) return state;

    return {
      ...state,
      [hash]: heatmap,
    };
  },
})(
  HeatmapsRequestTypes.GetUnnamedHeatmapRequest,
  HeatmapsRequestTypes.GetUnnamedHeatmapSuccess,
  HeatmapsRequestTypes.GetUnnamedHeatmapFailure,
);

type CreateUnnamedHeatmapRequestAction = RequestAction<
  HeatmapsRequestTypes.CreateUnnamedHeatmapRequest,
  CreateUnnamedHeatmapRequest
>;
export const isCreateUnnamedHeatmapRequestAction = (
  action: Action,
): action is CreateUnnamedHeatmapRequestAction =>
  (action as CreateUnnamedHeatmapRequestAction).type ===
  HeatmapsRequestTypes.CreateUnnamedHeatmapRequest;

type UnnamedHeatmapsRequestState = ReturnType<typeof unnamedHeatmapsRequests>;
const unnamedHeatmap = (
  state: UnnamedHeatmapsRequestState = {
    lastRequested: -1,
    lastResponded: -1,
    loading: false,
    error: false,
    data: null,
    safeMessage: null,
  },
  action: CreateUnnamedHeatmapRequestAction | DispatchedAction<any, any, any, any, any>,
): UnnamedHeatmapsRequestState => {
  if (isCreateUnnamedHeatmapRequestAction(action)) {
    if (!action.body || !action.body.heatmap) return state;
    return {
      ...state,
      data: {
        ...state.data,
        [action.body.hash]: action.body.heatmap,
      },
    };
  }
  return unnamedHeatmapsRequests(state, action);
};

const access = createAccessReducer(
  HeatmapsRequestTypes.GetHeatmapAccessRequest,
  HeatmapsRequestTypes.GetHeatmapAccessSuccess,
  HeatmapsRequestTypes.GetHeatmapAccessFailure,
);

const allHeatmaps = createRequestReducer<GetAllHeatmapsResponse>()(
  HeatmapsRequestTypes.GetAllHeatmapsRequest,
  HeatmapsRequestTypes.GetAllHeatmapsSuccess,
  HeatmapsRequestTypes.GetAllHeatmapsFailure,
);

const heatmapMetrics = createAppCacheReducer<HeatmapsDataType>()(
  HeatmapsRequestTypes.HeatmapsAppCacheIsLoading,
  HeatmapsRequestTypes.HeatmapsAppCacheLoadFailed,
  HeatmapsRequestTypes.HeatmapsAppCacheRefetching,
  HeatmapsRequestTypes.HeatmapsAppCacheHasData,
  HeatmapsRequestTypes.HeatmapsAppCacheFresh,
  HeatmapsRequestTypes.HeatmapsAppCacheSwap,
  HeatmapsRequestTypes.HeatmapsAppCacheClear,
)();

const heatmapZoneMetrics = createAppCacheReducer<HeatmapsClickAreaDataType>()(
  HeatmapsRequestTypes.HeatmapsClickAreaAppCacheIsLoading,
  HeatmapsRequestTypes.HeatmapsClickAreaAppCacheLoadFailed,
  HeatmapsRequestTypes.HeatmapsClickAreaAppCacheRefetching,
  HeatmapsRequestTypes.HeatmapsClickAreaAppCacheHasData,
  HeatmapsRequestTypes.HeatmapsClickAreaAppCacheFresh,
  HeatmapsRequestTypes.HeatmapsClickAreaAppCacheSwap,
  HeatmapsRequestTypes.HeatmapsClickAreaAppCacheClear,
)();

const heatmapWithPage = (event: Event): UnnamedHeatmap => ({
  heatmap: {
    id: '',
    name: '',
    description: '',
    representativePage: null,
    heatmapEvent: event,
    heatmapSettings: null,
  },
});

const omnisearch = (
  state: UnnamedHeatmap | null = null,
  action: SearchActions,
): UnnamedHeatmap | null => {
  switch (action.type) {
    case SearchActionTypes.AddRow:
      if (action.mode !== 'heatmap') return state;
      return heatmapWithPage(eventTypeToEventState(action.data as SearchGroup<EventType>));
  }
  return state;
};

type HeatmapsReducerState = {
  access: ReturnType<typeof access>;
  allHeatmaps: ReturnType<typeof allHeatmaps>;
  heatmaps: ReturnType<typeof heatmaps>;
  heatmapMetrics: ReturnType<typeof heatmapMetrics>;
  heatmapZoneMetrics: ReturnType<typeof heatmapZoneMetrics>;
  unnamedHeatmap: ReturnType<typeof unnamedHeatmap>;
  omnisearch: ReturnType<typeof omnisearch>;
};

export const heatmapsReducer = combineReducers<HeatmapsReducerState>({
  access,
  allHeatmaps,
  heatmaps,
  heatmapMetrics,
  heatmapZoneMetrics,
  unnamedHeatmap,
  omnisearch,
});
